import { useCallback, useEffect, useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import { api, formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { CalendarDays } from "lucide-react";
import { WaitlistDialog } from "../components/bookings/WaitlistDialog";
import { BookingForm } from "../components/bookings/BookingForm";
import { BookingsList } from "../components/bookings/BookingsList";
import { QrModal } from "../components/bookings/QrModal";

const isAdmin = (r) => r === "super_admin" || r === "property_manager";

export default function BookingsPage() {
  const { user } = useAuth();
  const admin = isAdmin(user?.role);
  const [plots, setPlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [form, setForm] = useState({ plot_id: "", check_in: "", check_out: "", guests: 1, notes: "", guest_emails: ["", "", "", ""] });
  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [qr, setQr] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [waitlistOpen, setWaitlistOpen] = useState(false);

  const load = useCallback(async () => {
    const [p, b] = await Promise.all([api.get("/cottage-plots"), api.get("/bookings")]);
    setPlots(p.data); setBookings(b.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Fetch blocked ranges for the selected cottage plot (next 12 months)
  useEffect(() => {
    if (!form.plot_id) { setAvailability(null); return; }
    let cancelled = false;
    const today = new Date();
    const from = format(today, "yyyy-MM-dd");
    const to = format(new Date(today.getFullYear() + 1, today.getMonth(), today.getDate()), "yyyy-MM-dd");
    api.get(`/plot-availability`, { params: { plot_id: form.plot_id, from, to } })
      .then(({ data }) => { if (!cancelled) setAvailability(data); })
      .catch(() => { if (!cancelled) setAvailability(null); });
    return () => { cancelled = true; };
  }, [form.plot_id]);

  const blockedDates = useMemo(() => {
    const s = new Set();
    if (!availability) return s;
    for (const r of availability.ranges || []) {
      // Half-open [check_in, check_out): night before check_out is the last blocked night
      const start = parseISO(r.check_in);
      const end = parseISO(r.check_out);
      const d = new Date(start);
      while (d < end) {
        s.add(format(d, "yyyy-MM-dd"));
        d.setDate(d.getDate() + 1);
      }
    }
    return s;
  }, [availability]);

  const cottagePlots = useMemo(() => plots.filter((p) => p.has_cottage), [plots]);

  // Live price preview
  useEffect(() => {
    if (!form.plot_id || !form.check_in || !form.check_out || form.check_in >= form.check_out) {
      setPreview(null); setPreviewErr("");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/bookings/preview", {
          plot_id: form.plot_id, check_in: form.check_in, check_out: form.check_out,
          guests: Number(form.guests) || 1, notes: "",
        });
        if (!cancelled) { setPreview(data); setPreviewErr(""); }
      } catch (err) {
        if (!cancelled) {
          setPreview(null);
          setPreviewErr(formatError(err.response?.data?.detail) || "Preview unavailable");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [form.plot_id, form.check_in, form.check_out, form.guests]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const emails = (form.guest_emails || []).map((s) => s.trim()).filter(Boolean);
      await api.post("/bookings", {
        plot_id: form.plot_id, check_in: form.check_in, check_out: form.check_out,
        guests: Number(form.guests), notes: form.notes,
        guest_emails: emails.length ? emails : undefined,
      });
      toast.success("Booking requested");
      setForm({ plot_id: "", check_in: "", check_out: "", guests: 1, notes: "", guest_emails: ["", "", "", ""] });
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Booking failed");
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (bid, status) => {
    try {
      await api.patch(`/bookings/${bid}`, { status });
      toast.success(`Booking ${status}`);
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      <div className="flex items-center gap-3 mb-1">
        <CalendarDays className="text-primary" />
        <h1 className="heading text-3xl md:text-4xl">Cottage bookings</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-8">{admin ? "Approve or reject stays." : "Reserve your cottage stays."}</p>

      {!admin && cottagePlots.length > 0 && (
        <BookingForm
          cottagePlots={cottagePlots}
          form={form}
          setForm={setForm}
          busy={busy}
          blockedDates={blockedDates}
          availability={availability}
          preview={preview}
          previewErr={previewErr}
          admin={admin}
          onSubmit={submit}
          onOpenWaitlist={() => setWaitlistOpen(true)}
        />
      )}

      <h2 className="heading text-xl mb-3">{admin ? "All bookings" : "Your bookings"}</h2>
      <BookingsList bookings={bookings} plots={plots} admin={admin} onSetStatus={setStatus} />

      <QrModal qr={qr} onClose={() => setQr(null)} />

      <WaitlistDialog
        open={waitlistOpen}
        onClose={() => setWaitlistOpen(false)}
        plotId={form.plot_id}
        plotLabel={cottagePlots.find((p) => p.id === form.plot_id)}
        defaultFrom={form.check_in}
        defaultTo={form.check_out}
        userEmail={user?.email}
        userName={user?.name}
      />
    </div>
  );
}
